import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Loader2, Link2, RefreshCw, Copy, CheckCircle2, AlertTriangle, Eye } from 'lucide-react';
import { formatToLocalDateTime } from '@/lib/dateUtils';

const fmt = (value) => (value ? formatToLocalDateTime(value) : 'Never');

const selectClass = 'w-full h-9 rounded-md border border-input bg-background px-2 text-sm';

/**
 * Event-level link to Kaizen Events: paid registrations become badges here automatically and are
 * sent by email + WhatsApp once. Super admins only (the backend enforces it too).
 */
const KaizenEventsLink = ({ eventId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [link, setLink] = useState(null);
  const [form, setForm] = useState({ base_url: '', campaign_id: '', api_key: '' });
  const [sendEmail, setSendEmail] = useState(true);
  const [sendWhatsapp, setSendWhatsapp] = useState(true);
  const [mapping, setMapping] = useState(null);
  const [preview, setPreview] = useState(null);

  const applyLink = useCallback((data) => {
    setLink(data);
    setForm({ base_url: data.base_url || '', campaign_id: data.campaign_id || '', api_key: '' });
    setSendEmail(data.auto_send_email ?? true);
    setSendWhatsapp(data.auto_send_whatsapp ?? true);
    if (data.mapping) setMapping(data.mapping);
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/events/${eventId}/kaizen-events-link`);
      applyLink(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load Kaizen Events link');
    } finally {
      setLoading(false);
    }
  }, [eventId, applyLink]);

  useEffect(() => { load(); }, [load]);

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const body = {
        base_url: form.base_url,
        campaign_id: form.campaign_id,
        auto_send_email: sendEmail,
        auto_send_whatsapp: sendWhatsapp,
        ...(form.api_key ? { api_key: form.api_key } : {}),
        ...(mapping ? { mapping } : {}),
        ...extra,
      };
      const res = await api.put(`/events/${eventId}/kaizen-events-link`, body);
      applyLink(res.data);
      toast.success(extra.enabled === true ? 'Link is on — badges will start arriving within 5 minutes'
        : extra.enabled === false ? 'Link paused' : 'Saved');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async (useMapping) => {
    setPreviewing(true);
    try {
      const res = await api.post(`/events/${eventId}/kaizen-events-link/preview`, { mapping: useMapping || null });
      setPreview(res.data);
      if (!mapping || !useMapping) setMapping(res.data.mapping_used);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const res = await api.post(`/events/${eventId}/kaizen-events-link/sync`);
      toast.success(res.data.message);
      setTimeout(load, 15000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed to start');
    } finally {
      setSyncing(false);
    }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success('Copied'); }
    catch { toast.error('Copy failed — select and copy manually'); }
  };

  const updatePassType = (index, patch) => {
    setMapping((m) => ({
      ...m,
      pass_types: m.pass_types.map((pt, i) => (i === index ? { ...pt, ...patch } : pt)),
    }));
  };

  if (loading) {
    return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  const fields = preview?.form_fields || [];
  const textFields = fields.filter((f) => f.type === 'text');
  const choiceFields = fields.filter((f) => (f.options || []).length > 0);
  const categories = preview?.categories || [];
  const stats = link?.last_sync_stats;
  const counts = link?.counts || {};

  return (
    <div className="space-y-4">
      {/* 1. Connection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2"><Link2 className="h-5 w-5" /> Kaizen Events link</span>
            {link?.enabled
              ? <span className="text-sm font-normal text-green-700 flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> On</span>
              : <span className="text-sm font-normal text-slate-500">Off</span>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600">
            Paid registrations from a Kaizen Events campaign become badges here automatically — one badge per pass —
            and are sent once by email and WhatsApp. In Kaizen Events, open the campaign → Settings → Kaizen QR → Generate key.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="kel-url">Kaizen Events API URL</Label>
              <Input id="kel-url" value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="kel-campaign">Campaign ID</Label>
              <Input id="kel-campaign" placeholder="944ddc34-…" value={form.campaign_id}
                onChange={(e) => setForm({ ...form, campaign_id: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="kel-key">Key {link?.api_key_set && <span className="text-green-700">(saved — type a new one only to replace it)</span>}</Label>
              <Input id="kel-key" type="password" autoComplete="off" placeholder="kqr_…" value={form.api_key}
                onChange={(e) => setForm({ ...form, api_key: e.target.value })} />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => save()} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save connection
            </Button>
            <Button variant="outline" onClick={() => runPreview(null)} disabled={previewing || !link?.api_key_set}>
              {previewing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
              Load form &amp; preview
            </Button>
          </div>
          <div className="rounded-md bg-slate-50 p-3 text-sm">
            <p className="text-slate-500">Webhook URL — paste into Kaizen Events for instant badges</p>
            <div className="flex items-center gap-2">
              <code className="text-xs break-all">{link?.webhook_url}</code>
              <button type="button" onClick={() => copy(link?.webhook_url || '')} aria-label="Copy webhook URL"
                className="text-slate-500 hover:text-slate-900"><Copy className="h-4 w-4" /></button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 2. Mapping */}
      {preview && mapping && (
        <Card>
          <CardHeader>
            <CardTitle>Pass types → badge categories</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">
              Campaign <strong>{preview.campaign?.name}</strong> — {preview.paid_registrations} paid registrations.
              The suggestion below was read from the form; check every row before turning the link on.
              Codes become part of ticket IDs (KE-R0001-ST1) and can't change once badges exist.
            </p>
            <div>
              <Label>Form field holding the pass type</Label>
              <select className={selectClass} value={mapping.pass_type_field_id || ''}
                onChange={(e) => setMapping({ ...mapping, pass_type_field_id: e.target.value })}>
                <option value="">— choose —</option>
                {choiceFields.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
              </select>
            </div>

            <div className="space-y-3">
              {(mapping.pass_types || []).map((pt, i) => (
                <div key={pt.option} className="rounded-lg border p-3 space-y-2">
                  <p className="font-medium text-sm">{pt.option}</p>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                    <div>
                      <Label className="text-xs">Badge category</Label>
                      <select className={selectClass} value={pt.category_id || ''}
                        onChange={(e) => updatePassType(i, { category_id: e.target.value || null })}>
                        <option value="">— choose —</option>
                        {categories.map((c) => <option key={c.category_id} value={c.category_id}>{c.category_name}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Number of passes from</Label>
                      <select className={selectClass} value={pt.count_field_id || ''}
                        onChange={(e) => updatePassType(i, { count_field_id: e.target.value || null })}>
                        <option value="">Fixed number</option>
                        {choiceFields.filter((f) => f.id !== mapping.pass_type_field_id)
                          .map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Fixed number</Label>
                      <Input type="number" min={1} max={50} disabled={!!pt.count_field_id}
                        value={pt.count_field_id ? '' : (pt.fixed_count ?? '')}
                        placeholder={pt.count_field_id ? '—' : '1'}
                        onChange={(e) => updatePassType(i, { fixed_count: e.target.value ? parseInt(e.target.value, 10) : null })} />
                    </div>
                    <div>
                      <Label className="text-xs">Code</Label>
                      <Input value={pt.code || ''} maxLength={6}
                        onChange={(e) => updatePassType(i, { code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })} />
                    </div>
                  </div>
                  <details className="text-sm">
                    <summary className="cursor-pointer text-slate-600">
                      Badge names from {(pt.name_field_ids || []).length} name field(s) — otherwise the registrant's name
                    </summary>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-1 mt-2">
                      {textFields.map((f) => (
                        <label key={f.id} className="flex items-center gap-2">
                          <input type="checkbox" checked={(pt.name_field_ids || []).includes(f.id)}
                            onChange={(e) => updatePassType(i, {
                              name_field_ids: e.target.checked
                                ? [...(pt.name_field_ids || []), f.id]
                                : (pt.name_field_ids || []).filter((id) => id !== f.id),
                            })} />
                          {f.label}
                        </label>
                      ))}
                    </div>
                  </details>
                </div>
              ))}
            </div>

            <Button variant="outline" onClick={() => runPreview(mapping)} disabled={previewing}>
              {previewing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Recalculate preview
            </Button>

            {preview.mapping_problems?.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                {preview.mapping_problems.map((p) => <p key={p}>• {p}</p>)}
              </div>
            )}
            {preview.summary && (
              <div className="rounded-md bg-slate-50 p-3 text-sm space-y-2">
                <p><strong>{preview.summary.badges}</strong> badges would be created:</p>
                <ul className="list-disc ml-5">
                  {Object.entries(preview.summary.per_category).map(([cat, n]) => <li key={cat}>{cat}: {n}</li>)}
                </ul>
                {preview.summary.problems.length > 0 && (
                  <div className="text-amber-800">
                    <p className="font-medium flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> Needs a look ({preview.summary.problems.length})</p>
                    <ul className="list-disc ml-5">
                      {preview.summary.problems.slice(0, 20).map((p) => (
                        <li key={p.registration}>{p.registration} {p.name ? `(${p.name})` : ''}: {p.warnings.join(' ')}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* 3. Sending + on/off */}
      <Card>
        <CardHeader><CardTitle>Sending</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Switch id="kel-email" checked={sendEmail} onCheckedChange={setSendEmail} />
            <Label htmlFor="kel-email">Email the badges (one email per registration, all badges attached)</Label>
          </div>
          <div className="flex items-center gap-3">
            <Switch id="kel-wa" checked={sendWhatsapp} onCheckedChange={setSendWhatsapp} />
            <Label htmlFor="kel-wa">WhatsApp each badge (uses this event's WhatsApp template)</Label>
          </div>
          <div className="flex flex-wrap gap-2">
            {link?.enabled ? (
              <Button variant="outline" onClick={() => save({ enabled: false })} disabled={saving}>Pause link</Button>
            ) : (
              <Button onClick={() => save({ enabled: true })} disabled={saving || !mapping}>
                Turn link on
              </Button>
            )}
            <Button variant="outline" onClick={() => save()} disabled={saving}>Save settings</Button>
            <Button variant="outline" onClick={syncNow} disabled={syncing || !link?.enabled}>
              {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Sync now
            </Button>
          </div>
          {!mapping && <p className="text-xs text-slate-500">Load the form and review the mapping before turning the link on.</p>}
        </CardContent>
      </Card>

      {/* 4. Status */}
      <Card>
        <CardHeader><CardTitle>Status</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-md bg-slate-50 p-3"><p className="text-slate-500">Badges</p><p className="text-lg font-semibold">{counts.total ?? 0}</p></div>
            <div className="rounded-md bg-slate-50 p-3"><p className="text-slate-500">Emailed</p><p className="text-lg font-semibold">{counts.email_sent ?? 0}</p></div>
            <div className="rounded-md bg-slate-50 p-3"><p className="text-slate-500">WhatsApp sent</p><p className="text-lg font-semibold">{counts.whatsapp_sent ?? 0}</p></div>
            <div className="rounded-md bg-slate-50 p-3"><p className="text-slate-500">Cancelled (refunds)</p><p className="text-lg font-semibold">{counts.cancelled ?? 0}</p></div>
          </div>
          {(counts.email_failed > 0 || counts.whatsapp_failed > 0) && (
            <p className="text-red-700">Failed sends: {counts.email_failed} email, {counts.whatsapp_failed} WhatsApp (retried up to 3 times; see the attendee list).</p>
          )}
          <p>
            Last sync: {fmt(link?.last_sync_at)} {link?.last_sync_reason && `(${link.last_sync_reason})`} —{' '}
            {link?.last_sync_status === 'error'
              ? <span className="text-red-700">failed: {link.last_sync_error}</span>
              : <span>{link?.last_sync_status || 'not run yet'}</span>}
          </p>
          {stats && (
            <p className="text-slate-600">
              Last run: {stats.created} new badges, {stats.cancelled} cancelled, {stats.emails_sent} emails,
              {' '}{stats.whatsapp_sent} WhatsApp messages.
            </p>
          )}
          {stats?.warnings?.length > 0 && (
            <details>
              <summary className="cursor-pointer text-amber-800">{stats.warnings.length} warning(s)</summary>
              <ul className="list-disc ml-5 mt-1 text-amber-900">
                {stats.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </details>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default KaizenEventsLink;
